import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { authService } from '../../services/authService'
import { CheckCircle2, Lock } from 'lucide-react'

interface ResetPasswordFormData {
    password: string
    confirmPassword: string
}

export const ResetPasswordPage: React.FC = () => {
    const { token } = useParams<{ token: string }>()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm<ResetPasswordFormData>({
        mode: 'onChange',
    })

    const password = watch('password')

    const onSubmit = async (data: ResetPasswordFormData) => {
        if (!token) {
            toast.error('Invalid reset token')
            return
        }

        try {
            setIsLoading(true)
            const response = await authService.resetPassword({
                token,
                password: data.password,
                confirmPassword: data.confirmPassword,
            })

            if (response.success) {
                setIsSuccess(true)
                toast.success(response.message || 'Password reset successfully')
                setTimeout(() => {
                    navigate('/login')
                }, 3000)
            } else {
                toast.error(response.message || 'Failed to reset password')
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Something went wrong')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-app-bg py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background Ambient Glows */}
            <div className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] bg-primary/8 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] bg-info/5 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none"></div>

            <div className="max-w-md w-full space-y-8 z-10">
                <div className="text-center">
                    <div className="flex justify-center mb-0">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-16 sm:h-20 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]" />
                        </Link>
                    </div>
                    <h2 className="text-3xl font-black font-heading text-white mt-[-8px] uppercase tracking-wide">Set New Password</h2>
                    <p className="mt-2 text-sm text-text-secondary font-medium">
                        {isSuccess
                            ? "Your password has been reset successfully."
                            : "Please enter your new password below."}
                    </p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>{isSuccess ? 'Success' : 'Reset Password'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!isSuccess ? (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                <Input
                                    label="New Password"
                                    type="password"
                                    placeholder="Minimum 8 characters"
                                    error={errors.password?.message}
                                    {...register('password', {
                                        required: 'Password is required',
                                        minLength: { value: 8, message: 'Minimum 8 characters' },
                                        pattern: {
                                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/,
                                            message: 'Password must include uppercase, lowercase, number, and special character',
                                        },
                                    })}
                                />

                                <Input
                                    label="Confirm New Password"
                                    type="password"
                                    placeholder="Repeat your password"
                                    error={errors.confirmPassword?.message}
                                    {...register('confirmPassword', {
                                        required: 'Please confirm your password',
                                        validate: (value) => value === password || 'Passwords do not match',
                                    })}
                                />

                                <Button type="submit" className="w-full" disabled={!isValid || isLoading} loading={isLoading}>
                                    {isLoading ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </form>
                        ) : (
                            <div className="text-center space-y-6 py-4">
                                <div className="flex justify-center">
                                    <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                                    </div>
                                </div>
                                <p className="text-text-secondary font-medium leading-relaxed">
                                    Your password has been successfully reset. You will be redirected to the login page in a few seconds.
                                </p>
                                <div className="pt-4">
                                    <Link to="/login">
                                        <Button className="w-full">Go to Login Now</Button>
                                    </Link>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
